import { Component, OnDestroy, OnInit } from '@angular/core';
import { NexsysMonitorService, OriginSourceData } from '../../service/nexsys-monitor.service';
import { NexsysOrderColumnsModel } from '../model/nexsys-order-columns.model';
import { CustomActionNexsysOrdersCellComponent } from './custom-action-grid-cell/custom-action-grid-cell.component';
import { BroadcastService, EditableGridComponent, ToasterType } from '@cv-invision/core';
import { CvDateFormatter } from '@cv-invision/cv.component-library.ux';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { SetNexsysOrderListFilters } from '../state/actions/nexsys-monitor.actions';
import { Store } from '@ngxs/store';
import { NexsysMonitorState } from '../state/nexsys-monitor.state';
import { ORIGIN } from '../model/nexsys-monitor.constant';
import { ICellRendererParams, RowNode } from 'ag-grid-community';
import { CustomActionShowProductModalCellComponent } from './custom-action-grid-cell/custom-action-show-product-modal-cell.component';
import { ProductsService } from '../../products/service/products.service';
import { Secure } from '@cv-invision/core';
import { PERMISSION } from '../../model/global-configuration.constant';

@Component({
	selector: 'cv-invision-nexsys-orders',
	templateUrl: './nexsys-orders.component.html',
	styleUrls: ['./nexsys-orders.component.scss']
})
export class NexsysOrdersComponent extends EditableGridComponent implements OnInit, OnDestroy {
	rowClassRules: any;
	filterOrders = {
		transactionId: '',
		providerId: '',
		orderId: '',
		provider: '',
		acuityId: '',
		filenumber: '',
		isAddendum: false,
		xpanseLoanNumber: '',
		limit: 10
	};
	private atComponentDestroy: Subject<any> = new Subject<any>();
	private filtersChanged: any = new Subject<string>();
	private subscription: Subscription;
	private readonly debounceTime = 500;
	providerSelected: string;
	providerList: Array<any> = [];
	clearProviderSelected: boolean;
	DEFAULT_GRID_HEIGHT = 445;
	protected INCREMENT_GRID_HEIGHT_ON_EXPAND = 49;

	filterProducts = {
		title: '',
		pagination: {
			totalCount: 0,
			offset: 0,
			limit: 100000
		}
	};

	productsList: any = [];

	constructor(
		private nexsysMonitorService: NexsysMonitorService,
		private broadcastService: BroadcastService,
		private store: Store,
		private dateService: CvDateFormatter,
		private productService: ProductsService,
	) {
		super(nexsysMonitorService);
		this.DEFAULT_ROW_HEIGHT = 40;
		this.rowClassRules = {
			'error-row': function (params: any): boolean {
				const {
					acuityResponseStatus,
					appscopeResponseStatus,
					inspectionResponseStatus,
					insideMapsResponseStatus
				} = params.data;
				return (
					acuityResponseStatus === 'FAILED' ||
					appscopeResponseStatus === 'FAILED' ||
					inspectionResponseStatus === 'FAILED' ||
					insideMapsResponseStatus === 'FAILED'
				);
			}
		};
	}

	ngOnInit(): void {
		this.addNexsysOrigin();
		this.addValutrustOrigin();
		this.addXpanseOrigin();
		this.addFNCOrigin();
		this.addEVOOrigin();
		this.addValuelinkOrigin();
		this.addConnexionsOrigin();
		this.addLendingQbOrigin();
		this.gridOptions.columnDefs = new NexsysOrderColumnsModel(this.dateService).columns;
		this.gridOptions.frameworkComponents = {
			customActionNexsysOrdersCellComponent: CustomActionNexsysOrdersCellComponent,
			customActionShowProductModalCellComponent: CustomActionShowProductModalCellComponent,
		};

		const nexsysOrderListFilters = this.store.selectSnapshot(
			NexsysMonitorState.getNexsysOrderListFilters
		);

		if (nexsysOrderListFilters) {

			if (nexsysOrderListFilters.provider && ['FNC', 'EVO'].includes(nexsysOrderListFilters.provider.toUpperCase())) {
				this.providerSelected = nexsysOrderListFilters.provider.toUpperCase();
			} else {
				this.providerSelected = this.capitalizeFirstLetter(nexsysOrderListFilters.provider);
			}

			this.onProviderSelected(this.providerSelected);
			this.filterOrders = { ...nexsysOrderListFilters };
		}
		this.loadGridData();

		this.subscription = this.filtersChanged
								.pipe(debounceTime(this.debounceTime))
								.pipe(takeUntil(this.atComponentDestroy))
								.subscribe(() => {
									this.gridApi.showLoadingOverlay();
									const filters = { ...this.filterOrders };
									this.store.dispatch(new SetNexsysOrderListFilters(filters));
									this.loadGridData();
								});
		// subscribe to the reload list event.
		this.nexsysMonitorService
			.getReloadListListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: OriginSourceData) => {
				if (data.reload) {
					this.loadGridData();
				}
				if (data.isDirty && data.origin) {
					this.providerSelected = data.origin;
					this.filterOrders.provider = this.providerSelected.toLowerCase();
					this.filtersChanged.next();
				}
			});
	}

	/**
	 * Expand Row node
	 * @param row
	 */
	expandRow(row: RowNode): void {
		if (this.willCollapseRow && this.willCollapseRow.rowIndex === row.rowIndex) {
			this.willCollapseRow = null;
		}
		if (!this.expandedRow || this.expandedRow.rowIndex !== row.rowIndex) {
			this.expandedRow = row;
			this.gridApi.resetRowHeights();

			if (this.gridData.length === 1) {
				this.gridHeight = 52 + this.getGridHeight(this.gridData.length);
			} else {
				this.gridHeight = this.gridHeight + this.INCREMENT_GRID_HEIGHT_ON_EXPAND;
			}
		}
	}

	/**
	 * This method was created to avoid the collision of the open/close events of a select list
	 * from a different column but the same row.
	 * Collapse the Row node
	 * @param row
	 */
	collapseRowWithDelay(row: RowNode): void {
		this.willCollapseRow = row;
		setTimeout(() => {
			if (this.willCollapseRow !== null) {
				this.collapseRow(this.willCollapseRow);
				this.willCollapseRow = null;
				if (this.gridData.length === 1) {
					this.gridHeight = 105;
				} else {
					this.gridHeight = this.gridHeight - this.INCREMENT_GRID_HEIGHT_ON_EXPAND;
				}
			}
		}, 200);
	}

	/**
	 * On cell updated change value of configuration and emit dirty change.
	 */
	onCellUpdated(params: ICellRendererParams, newData: any): void {
		const id = params.data.id;
		let match = this.config.find(item => item.id === id);
		if (match) {
			match = Object.assign(match, newData);
		}
		this.emitDirtyness();
	}

	/**
	 * set provider option value
	 */
	onProviderSelected($event: string): void {
		if ($event) {
			this.clearProviderSelected = false;
		}
		this.nexsysMonitorService.reloadList({ isHeaderChange: true, origin: $event });
		this.filterOrders.provider = $event ? $event.toLowerCase() : null;
		this.filterOrders.xpanseLoanNumber = '';
		this.filtersChanged.next();
	}

	/**
	 * set provider option value
	 */
	private capitalizeFirstLetter(string: string): string {
		return string ? string.charAt(0).toUpperCase() + string.slice(1) : null;
	}

	/**
	 * Sets the row height.
	 * @param params
	 */
	getRowHeight = (params: any): number => {
		const rowNode: RowNode = params.node;
		if (this.expandedRow && rowNode.rowIndex === this.expandedRow.rowIndex) {
			return 90 + this.getRowGridHeight(params.data);
		} else {
			if (this.gridData.length === 1) {
				return 62;
			} else {
				return this.DEFAULT_ROW_HEIGHT
			}
		}
	}

	/**
	 *  Call service endpoint to search orders.
	 */
	loadGridData(): void {
		this.productService
		.getProducts(this.filterProducts)
		.pipe(takeUntil(this.atComponentDestroy))
		.subscribe(
			result => {
				if (result.data) {
					for (var key in result.data) {
						this.productsList[result.data[key]['id']] = result.data[key]['title'];
					}
				}

				this.loadOrders();
			},
			error => console.error(error)
		);
	}

	/**
	 * Load orders
	 */
	loadOrders(): void {
		this.nexsysMonitorService
		.getNexsysOrders(this.filterOrders)
		.pipe(takeUntil(this.atComponentDestroy))
		.subscribe(
			result => {
				if (result.data) {
					result.data = result.data.map((product: any) => {
						product['productName'] = this.productsList[product.productId];
						return product;
					});

					this.gridData = result.data;
					this.gridPagination.pagination.limit = result.length;
					this.gridPagination.pagination.totalCount = result.length;
					if (this.gridData.length === 1) {
						this.gridHeight = 105;
					} else {
						this.gridHeight = this.getGridHeight(this.gridData.length);
					}

				}
			},
			error => {
				this.errorMsgFunction(error.error.error);
			}
		);
	}

	/**
	 * Returns the proper grid body height based on the number of total rows displayed.
	 * @param totalRows
	 */
	getGridHeight(totalRows: number): number {
		if (totalRows === 1 && this.gridData[0]) {
			return 82 + this.getRowGridHeight(this.gridData[0]);
		}

		const gridHeight = totalRows > 0
			? this.DEFAULT_ROW_HEIGHT * totalRows + 45 // header static height (set by css style)
			: this.DEFAULT_GRID_HEIGHT;

		return gridHeight;
	}

	/**
	 * Calculate row height
	 * @param orderData
	 * @returns
	 */
	private getRowGridHeight (orderData: any): number {
		let rowHeight = 0;

		const providers = ['nexsys', 'valutrust', 'xpanse', 'fnc', 'evo', 'valuelink', 'connexions', 'lendingqb'];

		if (orderData.provider === 'nexsys') {
			rowHeight += this.addNexsysHeight();
		}

		if (orderData.provider === 'valutrust') {
			rowHeight += this.addValutrustHeight();
		}

		if (orderData.provider === 'xpanse') {
			rowHeight += this.addXpanseHeight();
		}

		if (orderData.provider === 'fnc') {
			rowHeight += this.addFNCHeight();
		}

		if (orderData.provider === 'evo') {
			rowHeight += this.addEVOHeight();
		}

		if (orderData.provider === 'valuelink') {
			rowHeight += this.addValuelinkHeight();
		}

		if (orderData.provider === 'connexions') {
			rowHeight += this.addOrderExternalHeight();
		}

		if (orderData.origin === 'ClassOrderEntry') {
			rowHeight += this.addOrderExternalHeight();
		}

		if (orderData.provider === 'lendingqb') {
			rowHeight += this.addLendingQBHeight();
		}

		if (orderData.addendumInfo) {
			rowHeight += 35;
		}

		if (orderData.associatedProviderId) {
			rowHeight += 35;
		}

		if (orderData.associatedAddendumProviderId) {
			rowHeight += 35;
		}

		if (orderData.associatedAddendumProviderId) {
			rowHeight += 35;
		}

		return rowHeight;
	}

	@Secure(PERMISSION.NEXSYS)
	private addNexsysHeight(): number {
		return 35;
	}

	@Secure(PERMISSION.VALUTRUST)
	private addValutrustHeight(): number {
		return 35;
	}

	@Secure(PERMISSION.XPANSE)
	private addXpanseHeight(): number {
		return 35;
	}

	@Secure(PERMISSION.FNC)
	private addFNCHeight(): number {
		return 35;
	}

	@Secure(PERMISSION.EVO)
	private addEVOHeight(): number {
		return 35;
	}

	@Secure(PERMISSION.ORDERS_EXTERNAL)
	private addOrderExternalHeight(): number {
		return 35;
	}

	@Secure(PERMISSION.VALUE_LINK)
	private addValuelinkHeight(): number {
		return 35;
	}

	@Secure(PERMISSION.VALUE_LINK)
	private addLendingQBHeight(): number {
		return 35;
	}

	/**
	 * On Filters change, do the search.
	 */
	onFiltersChange(filter: any, $event: any): void {
		this.filterOrders[filter] = $event.value;
		this.filtersChanged.next();
	}

	/**
	 * On addendum change, do the search.
	 */
	onAddendumFilterChange($event: any): void {
		this.filterOrders.isAddendum = $event;
		this.filtersChanged.next();
	}

	/**
	 * Display error message wheter the oparation wast´n success
	 * @param error
	 */
	errorMsgFunction(error: string): void {
		const event = {
			error: error,
			autoClose: true
		};
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: event
		});
	}

	/**
	 * Clear all filters to it's default value.
	 */
	clearAllFilters(): void {
		this.clearProviderSelected = true;
		this.filterOrders.transactionId = '';
		this.filterOrders.provider = '';
		this.filterOrders.providerId = '';
		this.filterOrders.acuityId = '';
		this.filterOrders.filenumber = '';
		this.filterOrders.orderId = '';
		this.filterOrders.isAddendum = false;
		this.filtersChanged.next();
	}

	/**
	 * On component destroy
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	@Secure(PERMISSION.NEXSYS)
	private addNexsysOrigin(): void {
		this.providerList.push(ORIGIN.Nexsys);
	}

	@Secure(PERMISSION.VALUTRUST)
	private addValutrustOrigin(): void {
		this.providerList.push(ORIGIN.Valutrust);
	}

	@Secure(PERMISSION.XPANSE)
	private addXpanseOrigin(): void {
		this.providerList.push(ORIGIN.Xpanse);
	}

	@Secure(PERMISSION.FNC)
	private addFNCOrigin(): void {
		this.providerList.push(ORIGIN.FNC);
	}

	@Secure(PERMISSION.EVO)
	private addEVOOrigin(): void {
		this.providerList.push(ORIGIN.EVO);
	}

	@Secure(PERMISSION.VALUE_LINK)
	private addValuelinkOrigin(): void {
		this.providerList.push(ORIGIN.ValueLink);
	}

	@Secure(PERMISSION.CONNEXIONS)
	private addConnexionsOrigin(): void {
		this.providerList.push(ORIGIN.Connexions);
	}

	@Secure(PERMISSION.LENDING_QB)
	private addLendingQbOrigin(): void {
		this.providerList.push(ORIGIN.Lendingqb);
	}
}
